import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Typography } from "@mui/material";

import Input from "../../../components/Input";
import Button from "../../../components/Button";
import Select from "../../../components/Select";
import messages from "../../../assets/locale/messages";
import {
  addServiceRequest,
  editServiceRequest,
  getServiceDetailsRequest,
  getServiceDetailsResponse,
} from "../../../store/Services/actions";
import { getSpecialtiesRequest } from "../../../store/Specialties/actions";
import {
  ARABIC_REGEX,
  ENGLISH_REGEX,
  NAME_REGEX,
  ONE_SPACE_ONLY,
  ENGLISH_LOOKUP_NAME_REGEX,
  ARABIC_LOOKUP_NAME_REGEX,
  ATLEAST_TWO_ENGLISH_CHARACTERS,
  ATLEAST_TWO_ARABIC_CHARACTERS,
} from "../../../utils/Patterns";
import { setPageMainTitle } from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";

const AddEditServices = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const serviceDetails = useSelector((state) => state.services.serviceDetails);
  const { shared, services, pagesTitles } = messages[lang];
  const specialtiesList = useSelector((state) => state.specialties.list);

  const [specialtiesOptions, setSepecialtiesOptions] = useState([]);

  useEffect(() => {
    setPageMainTitle("servicesManagement");
    getSpecialtiesLookup();
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getServiceDetailsRequest({ id }));
    }
    return () => {
      dispatch(getServiceDetailsResponse({}));
    };
  }, [dispatch, id]);

  useEffect(() => {
    setSepecialtiesOptions(
      specialtiesList?.map((spec) => ({
        id: spec.id,
        label: lang === "en" ? spec.title_en : spec.title_ar,
      }))
    );
  }, [specialtiesList, lang]);

  useEffect(() => {
    if (values["specialty"]) {
      setFieldValue(
        "specialty",
        specialtiesOptions.find(
          (option) => option.id === values["specialty"].id
        )
      );
    }
  }, [lang, specialtiesOptions]);

  const getSpecialtiesLookup = () => {
    dispatch(
      getSpecialtiesRequest({
        page: -1,
      })
    );
  };

  const {
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      title_en: "",
      title_ar: "",
      specialty: null,
    },
    validationSchema: Yup.object({
      title_en: Yup.string()
        .required("enNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        // .matches(ENGLISH_REGEX, "englishLettersOnly"),
        .matches(ENGLISH_LOOKUP_NAME_REGEX, "englishLettersAndSpecialCharactersOnly")
        .matches(ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters"),
      title_ar: Yup.string()
        .required("arNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        // .matches(ARABIC_REGEX, "arabicLettersOnly"),
        .matches(ARABIC_LOOKUP_NAME_REGEX, "arabicLettersAndSpecialCharactersOnly")
        .matches(ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters"),
      specialty: Yup.object().nullable().required("specialtyRequired"),
    }),
    onSubmit: ({ title_en, title_ar, specialty }) => {
      !id
        ? dispatch(
            addServiceRequest(
              {
                title_en: title_en.trim(),
                title_ar: title_ar.trim(),
                specialty_id: specialty?.id,
              },
              ()=>history.push(ROUTE_PATHS.services)
          )
          )
        : dispatch(
            editServiceRequest({
              id: +id,
              data: {
                title_en: title_en.trim(),
                title_ar: title_ar.trim(),
                specialty_id: specialty?.id,
              },
            }
            ,() => {
              history.push(ROUTE_PATHS.services);
            }
          )
          );
    },
  });

  useEffect(() => {
    if (id && serviceDetails) {
      setFieldTouched({
        title_en: true,
        title_ar: true,
        specialty: true,
      });
      setValues({
        title_en: serviceDetails.title_en,
        title_ar: serviceDetails.title_ar,
        specialty: serviceDetails.specialty
          ? {
              id: serviceDetails.specialty.id,
              label:
                lang === "en"
                  ? serviceDetails.specialty.title_en
                  : serviceDetails.specialty.title_ar,
            }
          : null,
      });
    }
  }, [id, serviceDetails]);

  return (
    <div className="add-edit-service-page">
      <div className="d-flex mb-4">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-5" : "ms-5"}`}>
          {id ? pagesTitles.servicesEdit : pagesTitles.servicesAdd}
        </Typography>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="px-2 px-md-5 bg-white rounded-4">
          <div className="pt-4">
            <div className="row mt-4">
              <div className="col-12 col-md-6">
                <Input
                  id="title_en"
                  label={shared.lists.enTitle}
                  name="title_en"
                  type="text"
                  placeholder={services.addService.placeholders.enTitle}
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_en");
                      setFieldValue("title_en", value);
                    }
                  }}
                  value={values["title_en"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_en"] && errors["title_en"])}
                  errMsg={errors["title_en"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  id="title_ar"
                  label={shared.lists.arTitle}
                  name="title_ar"
                  type="text"
                  placeholder={services.addService.placeholders.arTitle}
                  required
                  inputWrapperClass="mb-4"
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_ar");
                      setFieldValue("title_ar", value);
                    }
                  }}
                  value={values["title_ar"]}
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_ar"] && errors["title_ar"])}
                  errMsg={errors["title_ar"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Select
                  name="Category Filter"
                  labelClassName="font-medium mb-2 text-primary"
                  label={services.addService.labels.specialty}
                  options={specialtiesOptions}
                  inputClassName="input-wrapper"
                  placeholder={
                    services.list.filter.filtersPlaceHolders.filterBySpecialty
                  }
                  required
                  value={values["specialty"]}
                  onChange={(value) => {
                    setFieldTouched("specialty");
                    setFieldValue("specialty", value);
                  }}
                  hasError={!!(touched["specialty"] && errors["specialty"])}
                  errMsg={errors["specialty"]}
                />
              </div>
            </div>
            <div className="row mt-4 py-4 d-flex justify-content-center">
              <div className="col-4">
                <Button
                  onClick={() => {}}
                  type="submit"
                  className="btn-secondary"
                  block
                  label={shared.save}
                  labelClass="px-4 fsize-16 font-medium text-white"
                  disabled={!dirty || !isValid}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditServices;
